import { useEffect } from 'react';
import { Card, Divider, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import Carousel from 'react-material-ui-carousel';
import dayjs from 'dayjs';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import HolidayList from './holiday-list';
import { AddHolidayPopup } from './add-holiday-popup';
import { DataNotFound, Scrollbar } from '@app/components';
import { useDialog, useRequestState } from '@app/hooks';
import { getHolidays } from '../slice';
import { selectHolidaysState } from '../selectors';
import { useAuthRolePermissions } from '@app/utils';

const HolidayListPage = () => {
  const addHolidayDialog = useDialog();
  const dispatch = useDispatch();
  const { hasPermission } = useAuthRolePermissions();
  const canAddHoliday = hasPermission('Holiday', 'w');

  useEffect(() => {
    dispatch(getHolidays());
  }, [dispatch]);

  const { data: holidays, loading } = useRequestState({
    stateSelector: selectHolidaysState,
    errorShown: true,
  });

  return (
    <>
      <Stack
        overflow="hidden"
        sx={{
          background:
            holidays && holidays?.length > 0
              ? `url(../assets/images/holidayBackground.png)`
              : 'none',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          height: '100vh',
          minHeight: '100vh',
        }}
      >
        <Header
          title={Strings.page_title.holidays}
          actionButtonTitle={canAddHoliday ? Strings.button.addHoliday : undefined}
          onClickActionButton={canAddHoliday ? addHolidayDialog.show : undefined}
        />
        <Divider />

        {holidays && holidays?.length > 0 ? (
          <>
            <Card
              sx={{
                borderRadius: '10px',
                width: { md: '97%', xs: '95%' },
                mt: 2,
                textAlign: 'center',
                mx: { md: 2, xs: 1 },
                height: '180px',
              }}
            >
              <Carousel
                autoPlay
                indicators
                animation="slide"
                interval={5000}
                duration={300}
                sx={{
                  justifyContent: 'center',
                  position: 'relative',
                }}
                indicatorContainerProps={{
                  style: {
                    position: 'absolute',
                    bottom: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1,
                  },
                }}
              >
                {holidays?.map((holiday, index) => (
                  <Stack
                    key={index}
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '180px',
                    }}
                  >
                    <img
                      src={holiday.image}
                      alt={holiday.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                      }}
                    />
                    <Stack
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '10px',
                        color: 'white',
                        padding: '5px',
                        borderRadius: '5px',
                      }}
                    >
                      <Typography variant="h6">{holiday.name}</Typography>
                    </Stack>
                    <Stack
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '20px',
                        color: 'white',
                        padding: '5px',
                        borderRadius: '5px',
                      }}
                    >
                      <Typography variant="h5">{dayjs(holiday.date).format('DD')}</Typography>
                      <Typography variant="h6">{dayjs(holiday.date).format('MMMM')}</Typography>
                    </Stack>
                  </Stack>
                ))}
              </Carousel>
            </Card>

            <Stack
              mx={2}
              direction="row"
              sx={{
                flexDirection: { xs: 'column', md: 'row' },
                flexGrow: 1,
                height: { xs: 'calc(100vh - 180px - 64px)', md: 'auto' },
                overflow: { xs: 'auto', md: 'hidden' },
              }}
            >
              <Scrollbar
                sx={{
                  height: { xs: '100%', md: 320 },
                  overflowY: { xs: 'auto', md: 'auto' },
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                  '-ms-overflow-style': 'none',
                }}
              >
                <HolidayList holidays={holidays} loading={loading} />
              </Scrollbar>
            </Stack>
          </>
        ) : (
          <Stack flexGrow={1} justifyContent="center" alignItems="center">
            <DataNotFound height={300} />
          </Stack>
        )}
      </Stack>

      {addHolidayDialog.visible && (
        <AddHolidayPopup open={addHolidayDialog.visible} onClose={addHolidayDialog.hide} />
      )}
    </>
  );
};

export { HolidayListPage };
